import { LoadingSpinner } from '@ctw/shared/components/loading-spinner';
import { useFeatureFlag } from '@ctw/shared/context/feature-flag-provider';
import { useHotloadStatus } from '@ctw/shared/hooks/use-hotload-status';
import { tw } from '@ctw/shared/utils/tailwind';
import { type PropsWithChildren, useEffect, useState } from 'react';

export interface HotloadGateProps extends PropsWithChildren {
  id?: string;
  testId?: string;
  upid?: string;
}

/**
 * Blocks its children behind a loading indicator until the patient's UPID is fully `hotloaded`,
 * then renders them. Passes children straight through — never trapping the user on a spinner —
 * when blocking is disabled (`ctw-hotload-blocking` off), there is no UPID, the poll timed out, or
 * the poll hard-errored. Unblocks on `hotloaded` (the load is finished), not on per-resource
 * membership in `loadedResourceTypes`.
 *
 * Deduped with every other `<HotloadGate>` via the shared `useHotloadStatus` query key, so
 * mounting this gate adds no extra network traffic.
 */
export const HotloadGate = ({ id, testId, upid, children }: HotloadGateProps) => {
  const hasHotloadBlocking = useFeatureFlag('ctw-hotload-blocking');
  const { isLoaded, hasTimedOut, isError } = useHotloadStatus(upid);

  const shouldPassThrough = !(hasHotloadBlocking && upid) || hasTimedOut || isError;
  const ready = shouldPassThrough || isLoaded;

  // Latch open per-patient: once content has been shown for this UPID, never re-block. Polling can
  // briefly report a non-loaded state again (a regressed poll, an errored refetch, a feature-flag
  // re-sync), and reverting to the spinner would unmount/remount the children — flickering the UI
  // in a loop. We only re-evaluate blocking when the patient (UPID) actually changes.
  const [unblockedUpid, setUnblockedUpid] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (ready && upid) {
      setUnblockedUpid(upid);
    }
  }, [ready, upid]);

  if (!(ready || unblockedUpid === upid)) {
    return (
      <div id={id} data-testid={testId} className={tw`h-full w-full`}>
        <LoadingSpinner centered={true} message="Loading..." />
      </div>
    );
  }

  return <>{children}</>;
};
